import * as React from 'react'

import { useDocumentStore } from '@/features/document'
import { AISearchResultResponse } from '@/schema'

import {
  DocumentGroup,
  getAllHighlightsWithVersions,
  groupAndSortSearchResults,
  HighlightWithVersionIndex,
} from '../utils/searchResultGrouping'

export type UseTFLSearchResultReturn = {
  groupedResults: DocumentGroup[]
  allHighlightsWithVersion: HighlightWithVersionIndex[]
}

const SCROLL_BEHAVIOR: ScrollBehavior = 'smooth'

/**
 * Hook that groups search results by document/version, flattens all highlights
 * with indices, and handles initial focus + "next focus" navigation with scroll.
 */
export const useTFLSearchResult = (
  results: AISearchResultResponse[] | undefined
): UseTFLSearchResultReturn => {
  const {
    toggleNextFocus,
    focusSearchResult,
    openDocuments,
    setFocusSearchResult,
    setOpenDocument,
    setActiveDocumentId,
    setVersionId,
  } = useDocumentStore()

  const groupedResults = React.useMemo(() => {
    if (!results?.length) return []
    return groupAndSortSearchResults(results)
  }, [results])

  const allHighlightsWithVersion = React.useMemo(
    () => getAllHighlightsWithVersions(groupedResults),
    [groupedResults]
  )

  const handleOpenDocument = React.useCallback(
    (documentId: string, displayName: string) => {
      if (!documentId || !document) return

      const isDocumentOpening = openDocuments.some(
        (doc) => doc.id === documentId
      )

      if (!isDocumentOpening) {
        setOpenDocument([
          ...openDocuments,
          { id: documentId, filename: displayName || '' },
        ])
      }

      setActiveDocumentId(documentId)
    },
    [openDocuments, setActiveDocumentId, setOpenDocument]
  )

  // Set initial focus to the first document/version when results load and no focus is set
  React.useEffect(() => {
    if (!groupedResults.length || !!focusSearchResult) return

    // Find the first highlight with keywords
    const firstHighlight = allHighlightsWithVersion.find(
      (i) => i.keywords.length
    )
    if (!firstHighlight) return

    setFocusSearchResult({
      displayName: firstHighlight.displayName,
      documentId: firstHighlight.documentId,
      versionId: firstHighlight.versionId,
      keywords: firstHighlight.keywords,
      index: 0,
    })
  }, [
    allHighlightsWithVersion,
    focusSearchResult,
    groupedResults.length,
    setFocusSearchResult,
  ])

  // When "next focus" is triggered, move to the next highlight in the flat list and scroll into view
  React.useEffect(() => {
    if (!focusSearchResult || !allHighlightsWithVersion.length) return

    const current = allHighlightsWithVersion.find(
      (h) =>
        h.documentId === focusSearchResult.documentId &&
        h.versionId === focusSearchResult.versionId &&
        h.highlightIndex === focusSearchResult.index
    )
    if (!current) return

    let nextGlobalIndex = current.globalIndex + 1
    if (nextGlobalIndex >= allHighlightsWithVersion.length) nextGlobalIndex = 0

    const nextItem = allHighlightsWithVersion[nextGlobalIndex]
    if (!nextItem) return

    setFocusSearchResult({
      displayName: nextItem.displayName,
      documentId: nextItem.documentId,
      versionId: nextItem.versionId,
      keywords: nextItem.keywords,
      index: nextItem.highlightIndex,
    })

    const selector = `[data-document-id="${nextItem.documentId}"][data-version-id="${nextItem.versionId}"][data-index="${nextItem.highlightIndex}"]`
    document.querySelector<HTMLElement>(selector)?.scrollIntoView({
      behavior: SCROLL_BEHAVIOR,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleNextFocus])

  React.useEffect(() => {
    if (!focusSearchResult) return

    // Open the document and set the version ID
    handleOpenDocument(
      focusSearchResult.documentId,
      focusSearchResult.displayName
    )
    setVersionId(focusSearchResult.documentId, focusSearchResult.versionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSearchResult])

  return React.useMemo(
    () => ({ groupedResults, allHighlightsWithVersion }),
    [groupedResults, allHighlightsWithVersion]
  )
}
